import { ApiError, OfflineError, pushBid } from './api.js';
import { countPending, db, pendingBids, type StoredBid } from './db.js';

/**
 * The sync queue.
 *
 * Contract, which the UI depends on:
 *  - Saving a bid locally never requires a network or a valid session.
 *  - An expired session PAUSES the queue and asks for re-login. Queued bids are
 *    never dropped, because a bid represents a real conversation with a customer.
 *  - Retrying is always safe: the server upserts on the bid's own UUID.
 */

export type SyncState =
  | { kind: 'idle'; pending: number }
  | { kind: 'syncing'; pending: number }
  | { kind: 'offline'; pending: number }
  | { kind: 'needs-login'; pending: number }
  | { kind: 'error'; pending: number; message: string };

type Listener = (state: SyncState) => void;

const listeners = new Set<Listener>();
let current: SyncState = { kind: 'idle', pending: 0 };
let running = false;

export function subscribeSync(listener: Listener): () => void {
  listeners.add(listener);
  listener(current);
  return () => listeners.delete(listener);
}

function emit(state: SyncState): void {
  current = state;
  for (const l of listeners) l(state);
}

export async function refreshPendingCount(): Promise<void> {
  emit({ ...current, pending: await countPending() } as SyncState);
}

/**
 * Attempts to push every pending bid. Safe to call at any time and from any
 * trigger — reconnect, app focus, a manual button, or after a save.
 */
export async function runSync(): Promise<SyncState> {
  if (running) return current;
  running = true;

  try {
    let pending = await pendingBids();
    if (pending.length === 0) {
      const state: SyncState = { kind: 'idle', pending: 0 };
      emit(state);
      return state;
    }

    emit({ kind: 'syncing', pending: pending.length });

    for (const bid of pending) {
      try {
        const outcome = await pushBid(bid);
        await db.bids.update(bid.id, {
          sync: 'synced',
          needsReview: outcome.needsReview,
          syncError: outcome.reviewNote ?? undefined,
        });
      } catch (err) {
        if (err instanceof OfflineError) {
          // Stop immediately: the rest will fail the same way. Nothing is lost.
          const state: SyncState = { kind: 'offline', pending: await countPending() };
          emit(state);
          return state;
        }

        if (err instanceof ApiError && err.status === 401) {
          // The session expired, quite possibly while the device was offline.
          // Pause and ask for a login; do not touch the queued bids.
          const state: SyncState = { kind: 'needs-login', pending: await countPending() };
          emit(state);
          return state;
        }

        if (err instanceof ApiError && err.permanent) {
          // Retrying cannot help, so stop retrying this one forever -- but keep
          // it on the device, visible, with the reason attached.
          await db.bids.update(bid.id, {
            sync: 'blocked',
            syncError: err.message,
          });
          continue;
        }

        const state: SyncState = {
          kind: 'error',
          pending: await countPending(),
          message: (err as Error).message,
        };
        emit(state);
        return state;
      }
    }

    const state: SyncState = { kind: 'idle', pending: await countPending() };
    emit(state);
    return state;
  } finally {
    running = false;
  }
}

/** Wires the browser's own signals to the queue. */
export function startSyncWatchers(): () => void {
  const onOnline = () => void runSync();
  const onVisible = () => {
    if (document.visibilityState === 'visible') void runSync();
  };

  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);
  // A periodic nudge for the case where the browser reports online but the
  // connection is actually dead -- common on a truck moving between towers.
  const timer = window.setInterval(() => void runSync(), 60_000);

  void runSync();

  return () => {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
    window.clearInterval(timer);
  };
}
